import React, { useEffect, useState } from 'react';

import { Grid } from '@mui/material';
import { toInteger } from 'lodash';
import { useParams } from 'react-router-dom';

import { NoPermissionMessage } from '../../components/PageLayout';
import SkeletonStack from '../../components/SkeletonStack';
import Tabs from '../../components/Tabs';
import { getCurrentUserId } from '../../services/auth';
import { PermissionType } from '../../types/common';
import { SubjectDetails } from '../../utils/common/constants';
import getSubjectsFromPermissions from '../../utils/common/getSubjectsFromPermissions';
import useTab from '../../utils/react/hooks/useTab';
import useUserPermissions from '../../utils/react/hooks/useUserPermissions';

import ExamsTab from './ExamsTab';
import IndividualExam from './IndividualExam';
import OverviewTab from './OverviewTab';

enum AnalyticsTab {
  Overview = 'overview',
  Individual = 'individual',
}

const ANALYTICS_TABS = [
  {
    key: AnalyticsTab.Overview,
    label: 'Overview',
  },
  {
    key: AnalyticsTab.Individual,
    label: 'Individual Exams',
  },
];

const AnalyticsPage = () => {
  const permissions = useUserPermissions();
  const userId = getCurrentUserId();

  const { studentId, subject, exam, attempt } = useParams();

  const [subjectsList, setSubjectsList] = useState<SubjectDetails[] | null>(
    permissions ? getSubjectsFromPermissions(permissions, PermissionType.Exam) : null,
  );
  const [subjectTab, setSubjectTab] = useState(subjectsList?.[0]?.key || '');
  const [analyticsTab, setAnalyticsTab] = useTab({ default: AnalyticsTab.Overview });

  useEffect(() => {
    const updateSubjects = async () => {
      if (permissions) {
        const list = getSubjectsFromPermissions(permissions, PermissionType.Exam);
        setSubjectsList(list);
        setSubjectTab(list[0]?.key);
      }
    };
    updateSubjects();
  }, [permissions]);

  if (!subjectsList || !permissions) {
    return (
      <SkeletonStack />
    );
  }

  if (subjectsList && subjectsList?.length === 0) {
    // If subjectsList has loaded but there are no subjects, show no permission message
    return (
      <NoPermissionMessage />
    );
  }

  if (subject && exam && attempt) {
    return (
      <IndividualExam
        subject={subject}
        studentId={studentId ?? userId ?? ''}
        examId={exam}
        attemptNum={toInteger(attempt)}
      />
    );
  }

  const renderTab = () => {
    switch (analyticsTab) {
      case AnalyticsTab.Overview:
        return (
          <OverviewTab subject={subjectTab} studentId={studentId ?? userId ?? ''} />
        );
      case AnalyticsTab.Individual:
        return (
          <ExamsTab subject={subjectTab} studentId={studentId ?? userId ?? ''} />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Grid container>
        <Grid item xs={12} sm={4}>
          <Tabs
            options={subjectsList || []}
            selectedKey={subjectTab}
            desktopDropdown
            onChange={(key) => {
              setSubjectTab(key);
            }}
          />
        </Grid>
        <Grid item xs={12} sm={8}>
          <Tabs
            options={ANALYTICS_TABS}
            selectedKey={analyticsTab}
            onChange={(key) => {
              setAnalyticsTab(key);
            }}
          />
        </Grid>
      </Grid>
      {renderTab()}
    </>
  );
};
export default AnalyticsPage;
